import { useState, useMemo } from "react";
import { useDashboardTheme } from "./themeHelper";
import { exportSkillReportPdf } from "@/utils/exportSkillReportPdf";

interface SkillReportPageProps {
  onBack: () => void;
  userData: any;
}

function getInitials(name: string = "") {
  return name.trim().split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = ["#6C63FF", "#FF6584", "#43C6AC", "#F7971E", "#56CCF2", "#BB6BD9", "#F2994A", "#27AE60"];
function avatarColor(name: string = "") {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function Avatar({ name, size = 34 }: { name: string; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: avatarColor(name),
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.35, fontWeight: 600, color: "#fff", flexShrink: 0,
    }}>
      {getInitials(name)}
    </div>
  );
}

function getScoreColor(score: number): string {
  if (score >= 90) return "#4ADE80";
  if (score >= 70) return "#A3E635";
  if (score >= 50) return "#FACC15";
  return "#F87171";
}

function SkillBar({ value, label }: { value: number; label: string }) {
  const { colors } = useDashboardTheme();
  const pct = Math.min(Math.max(value, 0), 100);
  const color = getScoreColor(pct);
  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: "16px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: colors.textMuted, fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 13, color, fontWeight: 700 }}>{pct}%</span>
      </div>
      <div style={{ height: 6, background: colors.ringTrack, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.6s ease" }} />
      </div>
    </div>
  );
}

function OverallScoreCell({ score, trend, isDark }: { score: number; trend: number; isDark: boolean }) {
  if (score <= 0) {
    return <span style={{ fontSize: 13, fontWeight: 600, color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)" }}>—</span>;
  }

  const scoreColor = getScoreColor(score);
  const isUp = trend >= 0;
  const trendBg = isUp
    ? (isDark ? "rgba(74, 222, 128, 0.16)" : "rgba(90, 157, 80, 0.12)")
    : (isDark ? "rgba(248, 113, 113, 0.16)" : "rgba(239, 68, 68, 0.12)");
  const trendColor = isUp ? (isDark ? "#4ADE80" : "#5a9d50") : (isDark ? "#F87171" : "#dc2626");

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 15, fontWeight: 800, color: scoreColor }}>
        {Math.round(score)}%
      </span>

      {trend !== 0 && (
        <span style={{
          fontSize: 11,
          fontWeight: 700,
          color: trendColor,
          background: trendBg,
          borderRadius: 6,
          padding: "2px 7px",
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          flexShrink: 0,
        }}>
          <span>{isUp ? "▲" : "▼"}</span>
          <span>{Math.abs(trend)}%</span>
        </span>
      )}
    </div>
  );
}

function ActivityBarChart({ history, isDark }: { history: number[]; isDark: boolean }) {
  const totalBars = 5;
  const recentHistory = (history && history.length > 0) ? history.slice(-totalBars) : [];

  return (
    <div style={{ display: "flex", gap: 4, alignItems: "flex-end", height: 28 }}>
      {Array.from({ length: totalBars }).map((_, i) => {
        const score = recentHistory[i];
        const hasValue = typeof score === "number" && !isNaN(score) && score > 0;
        const pct = hasValue ? Math.max(18, Math.min(100, score)) : 15;

        let bg = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0"; // default inactive track
        if (hasValue) {
          if (score >= 75) bg = isDark ? "#4ADE80" : "#5a9d50"; // Green
          else if (score >= 50) bg = isDark ? "#FACC15" : "#D97706"; // Yellow
          else bg = isDark ? "#F87171" : "#dc2626"; // Red
        }

        return (
          <div
            key={i}
            title={hasValue ? `Call ${i + 1}: ${Math.round(score)}%` : "No data"}
            style={{
              width: 8,
              borderRadius: 3,
              height: `${pct}%`,
              minHeight: 5,
              background: bg,
              transition: "height 0.4s ease, background 0.3s ease",
            }}
          />
        );
      })}
    </div>
  );
}

function SkillScoreBadge({ score }: { score: number }) {
  const { isDark } = useDashboardTheme();
  const color = score > 0 ? getScoreColor(score) : (isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.25)");
  return (
    <span style={{ fontSize: 13, fontWeight: 700, color }}>
      {score > 0 ? `${Math.round(score)}%` : "—"}
    </span>
  );
}

function avg(arr: number[]) {
  const v = arr.filter((x) => typeof x === "number" && !isNaN(x) && x > 0);
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : 0;
}

function isWithinDateRange(dateStr: any, range: string): boolean {
  if (range === "all") return true;
  if (!dateStr) return true;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return true;
  
  const limitDays = Number(range);
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - limitDays);
  return date.getTime() >= cutoff.getTime();
}

export default function SkillReportPage({ onBack, userData }: SkillReportPageProps) {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("overall");
  const [dateFilter, setDateFilter] = useState("all");

  const { teamSkills, reps } = useMemo(() => {
    const d = userData?.data || userData || {};
    const allPitches = d.tenantPitchMeta?.items || [];
    const userSummary = d.userPitchSummary || [];

    // Filter pitches by selected date range
    const pitches = allPitches.filter((p: any) =>
      isWithinDateRange(p.createdAt || p.updatedAt || p.date || p.timestamp, dateFilter)
    );

    // Filter pitches with scores
    const pitchesWithScores = pitches.filter((p: any) => p.categoryScores?.scores);

    const getSkillScore = (p: any, type: string): number => {
      const scores = p.categoryScores?.scores;
      if (!scores) return NaN;
      if (type === "discovery") {
        const disc = scores.discovery;
        return disc ? avg([disc.identified_needs, disc.listened_actively, disc.confirmed_understanding, disc.asked_relevant_questions]) : NaN;
      }
      if (type === "objections") {
        return scores.engagement?.handled_objections ?? NaN;
      }
      if (type === "product") {
        const pd = scores.pitch_delivery;
        return pd ? avg([pd.communicated_value, pd.structured_pitch, pd.used_clear_language]) : NaN;
      }
      if (type === "closing") {
        const cls = scores.closing;
        return cls ? avg([cls.created_urgency, cls.asked_for_next_step, cls.summarized_key_points]) : NaN;
      }
      return NaN;
    };

    const getPitchOverall = (p: any): number => {
      const disc = getSkillScore(p, "discovery");
      const obj = getSkillScore(p, "objections");
      const prod = getSkillScore(p, "product");
      const cls = getSkillScore(p, "closing");
      const active = [disc, obj, prod, cls].filter((v) => !isNaN(v) && v > 0);
      return active.length ? Math.round((avg(active) / 5) * 100) : NaN;
    };

    const skillAvgTeam = (type: string) => {
      const vals = pitchesWithScores.map((p: any) => getSkillScore(p, type)).filter((v: number) => !isNaN(v));
      return vals.length ? Math.round((avg(vals) / 5) * 100) : 0;
    };

    const teamSkills = {
      discovery: skillAvgTeam("discovery"),
      objections: skillAvgTeam("objections"),
      product: skillAvgTeam("product"),
      closing: skillAvgTeam("closing"),
    };

    // Rep-specific skills mapping
    const pitchesByRep: Record<string, any[]> = {};
    pitches.forEach((p: any) => {
      if (!p.userId) return;
      if (!pitchesByRep[p.userId]) pitchesByRep[p.userId] = [];
      pitchesByRep[p.userId].push(p);
    });

    const reps = userSummary.map((u: any) => {
      const userPitches = pitchesByRep[u.userId] || [];
      const userPitchesWithScores = userPitches.filter((p) => p.categoryScores?.scores);

      const sortedUserPitches = [...userPitchesWithScores].sort((a, b) => {
        const tA = new Date(a.createdAt || a.date || a.timestamp || 0).getTime();
        const tB = new Date(b.createdAt || b.date || b.timestamp || 0).getTime();
        return tA - tB;
      });

      const skillAvgRep = (type: string) => {
        const vals = userPitchesWithScores.map((p) => getSkillScore(p, type)).filter((v) => !isNaN(v));
        return vals.length ? Math.round((avg(vals) / 5) * 100) : 0;
      };

      const discovery = skillAvgRep("discovery");
      const objections = skillAvgRep("objections");
      const product = skillAvgRep("product");
      const closing = skillAvgRep("closing");

      // Filter and average actual active skill categories
      const activeScores = [discovery, objections, product, closing].filter((s) => s > 0);
      const overall = activeScores.length ? Math.round(avg(activeScores)) : 0;

      // History scores for mini sparkline graph
      const historyScores = sortedUserPitches.map((p) => getPitchOverall(p)).filter((v) => !isNaN(v) && v > 0);
      const history = historyScores.slice(-6);

      // Trend calculation
      let trend = 0;
      if (historyScores.length >= 2) {
        const mid = Math.floor(historyScores.length / 2);
        const olderAvg = avg(historyScores.slice(0, mid));
        const recentAvg = avg(historyScores.slice(mid));
        trend = Math.round(recentAvg - olderAvg);
      }

      return {
        ...u,
        discovery,
        objections,
        product,
        closing,
        overall,
        history,
        trend,
      };
    });

    return { teamSkills, reps };
  }, [userData, dateFilter]);

  const filtered = useMemo(() => {
    return reps
      .filter((r : any) => (r.name || "").toLowerCase().includes(search.toLowerCase()))
      .sort((a : any , b : any) => {
        if (sortBy === "overall") return b.overall - a.overall;
        if (sortBy === "discovery") return b.discovery - a.discovery;
        if (sortBy === "objections") return b.objections - a.objections;
        if (sortBy === "product") return b.product - a.product;
        if (sortBy === "closing") return b.closing - a.closing;
        if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
        return 0;
      });
  }, [reps, search, sortBy]);

  const { colors, isDark } = useDashboardTheme();
  const styles = {
    root: { minHeight: "100vh", background: colors.bg, color: colors.text, fontFamily: "'Inter', -apple-system, sans-serif", padding: "28px 36px" },
    header: { display: "flex", alignItems: "center", gap: 16, marginBottom: 28 },
    backBtn: { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: "8px 14px", color: colors.textMuted, fontSize: 13, cursor: "pointer" },
    input: { background: colors.inputBg, border: `1px solid ${colors.inputBorder}`, borderRadius: 8, padding: "8px 14px", color: colors.inputText, fontSize: 13, outline: "none", minWidth: 240, marginBottom: 20 },
    table: { width: "100%", borderCollapse: "collapse" as const },
    th: { fontSize: 11, color: colors.textMuted, fontWeight: 600, letterSpacing: 0.8, padding: "12px 16px", textAlign: "left" as const, borderBottom: `1px solid ${colors.border}`, cursor: "pointer", userSelect: "none" as const },
    td: { padding: "14px 16px", borderBottom: `1px solid ${colors.borderAlt}`, verticalAlign: "middle" },
    card: { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 16, overflow: "hidden" },
  };

  const sortHeader = (key: string, label: string) => (
    <th style={{ ...styles.th, color: sortBy === key ? "#6C63FF" : colors.textMuted }}
      onClick={() => setSortBy(key)}>{label} {sortBy === key ? "↓" : ""}</th>
  );

  return (
    <div style={styles.root}>
      {/* Header */}
      <div style={{ ...styles.header, justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button style={styles.backBtn} onClick={onBack}>← Back to Dashboard</button>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.text, margin: 0 }}>Skill Report</h1>
            <p style={{ fontSize: 13, color: colors.textMuted, margin: "3px 0 0" }}>Team category skill index mapping</p>
          </div>
        </div>

        <button
          onClick={() => exportSkillReportPdf({ teamSkills, reps: filtered, dateFilter })}
          style={{
            background: isDark ? "#B8F890" : "#5a9d50",
            color: isDark ? "#0a1608" : "#ffffff",
            border: "none",
            borderRadius: 8,
            padding: "10px 18px",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            transition: "all 0.2s ease",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="12" x2="12" y2="3" />
          </svg>
          Download Report
        </button>
      </div>

      {/* Team Skill Summary Progress Bars */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 28 }}>
        <SkillBar label="Discovery" value={teamSkills.discovery} />
        <SkillBar label="Objection Handling" value={teamSkills.objections} />
        <SkillBar label="Product Knowledge" value={teamSkills.product} />
        <SkillBar label="Closing" value={teamSkills.closing} />
      </div>

      {/* Controls Bar: Search & Time Filter */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <input
          style={{ ...styles.input, marginBottom: 0 }}
          placeholder="Search rep by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 13, color: colors.textMuted, fontWeight: 600 }}>Time Filter:</span>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={{
              background: colors.inputBg,
              border: `1px solid ${colors.inputBorder}`,
              borderRadius: 8,
              padding: "8px 14px",
              color: colors.inputText,
              fontSize: 13,
              fontWeight: 600,
              outline: "none",
              cursor: "pointer",
              fontFamily: "'Inter', -apple-system, sans-serif",
            }}
          >
            <option value="7" style={{ background: isDark ? "#0f0f1a" : "#ffffff", color: colors.text }}>Last 7 Days</option>
            <option value="15" style={{ background: isDark ? "#0f0f1a" : "#ffffff", color: colors.text }}>Last 15 Days</option>
            <option value="30" style={{ background: isDark ? "#0f0f1a" : "#ffffff", color: colors.text }}>Last 30 Days (1 Month)</option>
            <option value="60" style={{ background: isDark ? "#0f0f1a" : "#ffffff", color: colors.text }}>Last 2 Months</option>
            <option value="90" style={{ background: isDark ? "#0f0f1a" : "#ffffff", color: colors.text }}>Last 3 Months</option>
            <option value="all" style={{ background: isDark ? "#0f0f1a" : "#ffffff", color: colors.text }}>All Time</option>
          </select>
        </div>
      </div>

      {/* Reps Breakdown Table */}
      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              {sortHeader("name", "REP")}
              {sortHeader("discovery", "DISCOVERY")}
              {sortHeader("objections", "OBJECTION HANDLING")}
              {sortHeader("product", "PRODUCT KNOWLEDGE")}
              {sortHeader("closing", "CLOSING")}
              {sortHeader("overall", "OVERALL AVG")}
              {sortHeader("trend", "ACTIVITY BARS")}
            </tr>
          </thead>
          <tbody>
            {filtered.map((rep : any, i : number) => (
              <tr key={rep.userId}
                style={{ background: i % 2 === 0 ? "transparent" : colors.surfaceAlt }}
                onMouseEnter={(e) => e.currentTarget.style.background = colors.rowHoverAlt}
                onMouseLeave={(e) => e.currentTarget.style.background = i % 2 === 0 ? "transparent" : colors.surfaceAlt}
              >
                <td style={styles.td}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar name={rep.name} size={32} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>{rep.name}</div>
                    </div>
                  </div>
                </td>
                <td style={styles.td}><SkillScoreBadge score={rep.discovery} /></td>
                <td style={styles.td}><SkillScoreBadge score={rep.objections} /></td>
                <td style={styles.td}><SkillScoreBadge score={rep.product} /></td>
                <td style={styles.td}><SkillScoreBadge score={rep.closing} /></td>
                <td style={styles.td}>
                  <OverallScoreCell score={rep.overall} trend={rep.trend} isDark={isDark} />
                </td>
                <td style={styles.td}>
                  <ActivityBarChart history={rep.history} isDark={isDark} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: 48, color: colors.textMutedLight, fontSize: 13 }}>
            No reps match your search query
          </div>
        )}
      </div>
    </div>
  );
}
